import type { DocumentRef, RenewalRow, ReportResult } from "@agency/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { useState } from "react";
import { callAction } from "../api";
import { useToast } from "../components/Toast";
import { Button, Card, EmptyState, ErrorState, Input, LoadingState, Tabs } from "../components/ui";
import { useLocale, useT } from "../i18n";
import { money, pct, shortDate } from "../theme";

type ReportId = "book_of_business" | "production" | "retention" | "commissions" | "activity" | "accountability" | "payments" | "lead_sources";
const REPORTS: ReportId[] = ["book_of_business", "production", "retention", "commissions", "activity", "accountability", "payments", "lead_sources"];

export function ReportsScreen() {
  const t = useT();
  const { locale } = useLocale();
  const { toast } = useToast();
  const [report, setReport] = useState<ReportId>("book_of_business");
  const [from, setFrom] = useState(new Date(Date.now() - 30 * 86_400_000).toISOString().slice(0, 10));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const ranged = report === "production" || report === "activity" || report === "accountability" || report === "payments" || report === "lead_sources";

  const q = useQuery({
    queryKey: ["report", report, ranged ? from : "", ranged ? to : ""],
    queryFn: async () => (await callAction<"agency.report.get", ReportResult>("agency.report.get", { report, from: ranged ? from : undefined, to: ranged ? to : undefined })).result,
  });
  const exp = useMutation({
    mutationFn: () => callAction<"agency.export.create", DocumentRef>("agency.export.create", { view: report, format: "csv" }),
    onSuccess: (r) => {
      toast({ title: t("common.exported"), tone: "success" });
      window.open(r.result.url, "_blank", "noopener");
    },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });

  const fmt = (kind: ReportResult["columns"][number]["kind"], v: string | number | null) => {
    if (v == null || v === "") return "—";
    if (kind === "money") return money(typeof v === "number" ? v : Number(v));
    if (kind === "pct") return pct(typeof v === "number" ? v : Number(v), 1);
    if (kind === "date") return shortDate(String(v), locale);
    if (kind === "number") return typeof v === "number" ? v.toLocaleString() : String(v);
    return String(v);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold">{t("reports.title")}</h1>
          <p className="text-ink/60">{t("reports.subtitle")}</p>
        </div>
        <Button variant="outline" onClick={() => exp.mutate()} loading={exp.isPending}><Download className="h-4 w-4" />{t("common.export")}</Button>
      </div>

      <Tabs<ReportId> value={report} onChange={setReport} tabs={REPORTS.map((r) => ({ id: r, label: t(`reports.${r}`) }))} />

      {ranged ? (
        <Card className="p-4 grid gap-3 sm:grid-cols-2 max-w-xl">
          <Input type="date" label={t("reports.from")} value={from} onChange={(e) => setFrom(e.target.value)} />
          <Input type="date" label={t("reports.to")} value={to} onChange={(e) => setTo(e.target.value)} />
        </Card>
      ) : null}

      {report === "retention" ? <AgencyPulse totals={q.data?.totals} /> : null}

      {q.isLoading ? <LoadingState /> : null}
      {q.isError ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : null}
      {q.data && q.data.rows.length === 0 ? <EmptyState title={t("reports.none")} /> : null}
      {q.data && q.data.rows.length > 0 ? (
        <Card className="overflow-x-auto">
          <div className="flex items-center justify-between px-4 pt-3">
            <h2 className="text-lg font-bold">{q.data.title[locale]}</h2>
            <span className="text-xs text-ink/50">{t("reports.generated")} {shortDate(q.data.generatedAt, locale)}</span>
          </div>
          <table className="w-full text-sm">
            <thead className="text-left text-ink/55">
              <tr>
                {q.data.columns.map((c) => <th key={c.key} className={c.kind === "money" || c.kind === "number" || c.kind === "pct" ? "px-4 py-3 text-right" : "px-4 py-3"}>{c.label[locale]}</th>)}
              </tr>
            </thead>
            <tbody>
              {q.data.rows.map((row, i) => (
                <tr key={i} className="border-t border-ink/5">
                  {q.data!.columns.map((c) => <td key={c.key} className={c.kind === "money" || c.kind === "number" || c.kind === "pct" ? "px-4 py-2 text-right tabular-nums" : "px-4 py-2"}>{fmt(c.kind, row[c.key] ?? null)}</td>)}
                </tr>
              ))}
            </tbody>
            {Object.keys(q.data.totals).length ? (
              <tfoot>
                <tr className="border-t-2 border-ink/15 font-bold bg-surface">
                  {q.data.columns.map((c, i) => <td key={c.key} className={c.kind === "money" || c.kind === "number" || c.kind === "pct" ? "px-4 py-2 text-right tabular-nums" : "px-4 py-2"}>{i === 0 && q.data!.totals[c.key] == null ? t("reports.totals") : fmt(c.kind, q.data!.totals[c.key] ?? null)}</td>)}
                </tr>
              </tfoot>
            ) : null}
          </table>
        </Card>
      ) : null}
    </div>
  );
}

/** Retention %, renewals due in 30 days, average premium change. Pulls from report totals when present, else from the renewals action. */
function AgencyPulse({ totals }: { totals?: Record<string, string | number | null> }) {
  const t = useT();
  const ren = useQuery({
    queryKey: ["renewals", 30],
    queryFn: async () => (await callAction<"agency.policy.renewals", RenewalRow[]>("agency.policy.renewals", { withinDays: 30 })).result,
    staleTime: 60_000,
  });
  const due30 = totals?.due30 ?? ren.data?.length ?? null;
  const deltas = (ren.data ?? []).map((r) => r.premiumDeltaPct).filter((x): x is number => x != null);
  const avgChange = totals?.avgPremiumChangePct ?? (deltas.length ? deltas.reduce((a, b) => a + b, 0) / deltas.length : null);
  const retention = totals?.retentionPct ?? null;
  const tile = (label: string, value: string) => (
    <div className="rounded-2xl bg-white border border-ink/5 p-4 shadow-card">
      <p className="text-xs uppercase tracking-wide text-ink/55">{label}</p>
      <p className="mt-1 text-2xl font-bold heading">{value}</p>
    </div>
  );
  return (
    <section>
      <h2 className="text-sm font-bold uppercase tracking-wide text-ink/60 mb-2">{t("reports.pulse")}</h2>
      <div className="grid gap-3 sm:grid-cols-3">
        {tile(t("reports.pulse.retention"), retention == null ? "—" : `${Number(retention).toFixed(0)}%`)}
        {tile(t("reports.pulse.due30"), due30 == null ? "—" : String(due30))}
        {tile(t("reports.pulse.avgChange"), avgChange == null ? "—" : pct(Number(avgChange), 1))}
      </div>
    </section>
  );
}
